// Route -> human page label for the presence heartbeat (FCA-6979).
//
// The heartbeat already reports the raw path, which the backend normalises to a
// route template. A label is sent alongside it because a path is a developer
// artefact: an admin scanning "who is online and what are they doing" reads
// "Recommendations" instantly and "/recommendations/recommendationinfo/3f9.../edit"
// not at all.
//
// Deliberately a lookup table rather than a router integration. Reusing the
// router's matched route would need this module to know about the `Switch` in
// `App.js`, and the heartbeat is mounted above it precisely to stay decoupled.
// The cost of the table is that a new section ships without a label until
// someone adds one here — which is why an unmatched path returns "" and lets
// the backend fall back to the normalised path rather than guessing.
//
// Labels are matched to the SideMenu wording so the page reads the same as the
// navigation an admin is looking at.

/**
 * Characters the backend accepts in a page label. Anything else is rejected
 * server-side, so labels must stay plain ASCII words plus light punctuation —
 * in particular no em-dashes, which this codebase otherwise uses freely.
 */
export const PAGE_NAME_PATTERN = /^[A-Za-z0-9 &/()\-.,']{1,80}$/;

/**
 * Ordered prefix table. First match wins, so more specific paths must come
 * first (`/settings/active-sessions` before `/settings`, `/systemtables`
 * before `/system`).
 *
 * `pattern` is either a lowercase path prefix matched on segment boundaries —
 * so `/project` never swallows `/project-template` — or a `RegExp` tested
 * against the lowercased path. Lowercasing is not cosmetic: SideMenu links to
 * `/Dashboard`, `/Project` and `/Client` with capitals, so the browser's real
 * pathname casing varies by how the user arrived.
 */
export const PAGE_NAME_RULES = [
    { pattern: /^\/(login|sso|forgot_password|reset_password|auth\/callback)(\/|$)/, name: "Sign In" },
    { pattern: "/home", name: "Landing Page" },
    { pattern: "/dashboard", name: "Dashboard" },
    { pattern: "/embedded-dashboard", name: "Dashboard" },
    { pattern: "/settings/active-sessions", name: "Active Sessions" },
    { pattern: "/settings/logs", name: "Logs" },
    { pattern: "/settings/user", name: "Users" },
    { pattern: "/settings/userpermissions", name: "User Permissions" },
    { pattern: "/settings/knowledge", name: "Knowledge Base" },
    { pattern: "/settings/notifications", name: "Notifications" },
    { pattern: "/settings/email", name: "Emails" },
    { pattern: "/settings/buildingtype", name: "Building Type" },
    { pattern: "/settings", name: "Settings" },
    { pattern: "/project-capital-request", name: "Capital Requests" },
    { pattern: "/capital-request", name: "Capital Requests" },
    { pattern: "/project-template", name: "FCA Survey Projects" },
    { pattern: "/global-project-template", name: "FCA Survey Templates" },
    { pattern: "/project", name: "FCA Projects" },
    { pattern: "/efci", name: "EFCI Benchmarking" },
    { pattern: "/initiatives", name: "Project Initiatives" },
    { pattern: "/smartcharts", name: "Smart Reports & Charts" },
    { pattern: "/region", name: "Regions" },
    { pattern: "/site", name: "Sites" },
    { pattern: "/buildingaddition", name: "Building Additions" },
    { pattern: "/buildinglease", name: "Building Leases" },
    { pattern: "/buildingtype", name: "Building Type" },
    { pattern: "/building", name: "Buildings" },
    { pattern: "/floor", name: "Floors" },
    { pattern: "/mobrecoms", name: "Mobile Recommendations" },
    { pattern: "/recommendationtemplate", name: "Recommendation Templates" },
    { pattern: "/recommendations", name: "Recommendations" },
    { pattern: "/client", name: "Clients" },
    { pattern: "/consultancy", name: "Consultancies" },
    { pattern: "/documents", name: "Document Management" },
    { pattern: "/document-settings", name: "Document Settings" },
    { pattern: "/images", name: "Image Management" },
    { pattern: "/reports", name: "Reports" },
    { pattern: "/asset-survey-project", name: "Asset Survey Projects" },
    { pattern: "/survey-template", name: "Asset Survey Templates" },
    { pattern: "/survey-settings", name: "Image & Sync Settings" },
    { pattern: "/surveyor-profiles", name: "Surveyor Profiles" },
    { pattern: "/assetmanagement", name: "Asset Management" },
    { pattern: "/asset-settings", name: "Asset Settings" },
    { pattern: "/assets", name: "Assets" },
    { pattern: "/energymanagement", name: "Energy Management" },
    { pattern: "/accounts", name: "Accounts" },
    { pattern: "/meter", name: "Meter" },
    { pattern: "/median", name: "Property Type Median EUIs (US)" },
    { pattern: "/systemtables", name: "System Tables" },
    { pattern: "/system", name: "System" },
    { pattern: "/subsystem", name: "Sub System" },
    { pattern: "/trade", name: "Trade" },
    { pattern: "/chartsandgraphs", name: "Charts & Graphs" },
    { pattern: "/charttemplates", name: "Export Templates" },
    { pattern: "/chartproperties", name: "Export Properties" },
    { pattern: "/dynamic-field-settings", name: "Dynamic Fields" },
    { pattern: "/task-manager", name: "Task Manager" }
];

const matchesPattern = (pattern, path) => {
    if (pattern instanceof RegExp) {
        return pattern.test(path);
    }
    return path === pattern || path.startsWith(`${pattern}/`);
};

/**
 * Resolve a browser pathname to a short label for the sessions table.
 *
 * @param {string} pathname
 * @returns {string} label, or "" when nothing matches — the backend then falls
 *   back to the normalised path, which is worse to read but never wrong.
 */
export const resolvePageName = pathname => {
    if (typeof pathname !== "string" || !pathname) {
        return "";
    }
    const path = pathname.split("?")[0].split("#")[0].toLowerCase().replace(/\/+$/, "") || "/";
    const rule = PAGE_NAME_RULES.find(entry => matchesPattern(entry.pattern, path));
    return rule ? rule.name : "";
};
